"use client";

import {
  AddCircle,
  RemoveCircle,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useCallback, useRef, useState, memo, useEffect, useMemo } from "react";
import { useDrag, useDrop } from "react-dnd";
import throttle from "lodash/throttle";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { FileWithPreview, useFiles } from "@contexts/index";

import {
  N_OF_PHOTOS_BEGIN,
  N_OF_PHOTOS_END,
} from "src/constants/photo-preview.constant";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import PhotoModal from "./photo-modal";
import { Grid as VirtualGrid, type CellComponentProps } from "react-window";
import {
  GRID_VIEWPORT_HEIGHT,
  photoGridLayout,
  VIRTUALIZE_AFTER,
} from "./grid-layout";
import { useLocale } from "next-intl";

type PhotoProps = {
  photo: FileWithPreview;
  index: number;
  movePhoto: (dragIndex: number, hoverIndex: number) => void;
  onDoubleClick: (photo: FileWithPreview) => void;
  incrementCounter: (index: number) => void;
  decrementCounter: (index: number) => void;
  selectedPhotos: number[];
  setSelectedPhotos: React.Dispatch<React.SetStateAction<number[]>>;
  isSelected: boolean;
  allowEdit: boolean;
  disable: boolean;
};

const arePropsEqual = (prevProps: PhotoProps, nextProps: PhotoProps) => {
  return (
    prevProps.index === nextProps.index &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.photo.preview === nextProps.photo.preview &&
    prevProps.photo.count === nextProps.photo.count &&
    prevProps.disable === nextProps.disable &&
    prevProps.allowEdit === nextProps.allowEdit &&
    prevProps.selectedPhotos.length === nextProps.selectedPhotos.length &&
    prevProps.selectedPhotos.every(
      (val: number, idx: number) => val === nextProps.selectedPhotos[idx]
    )
  );
};

export const Photo: React.FC<PhotoProps> = memo(
  ({
    photo,
    index,
    movePhoto,
    onDoubleClick,

    incrementCounter,
    decrementCounter,
    selectedPhotos,
    isSelected,
    allowEdit,
    disable,
  }) => {
    const ref = useRef<HTMLDivElement>(null);
    useAppTranslate();
    const [{ isDragging }, drag] = useDrag({
      type: "photo",
      item: () => {
        return {
          index,
          isSelected: isSelected || selectedPhotos.includes(index),
        };
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    });

    const [, drop] = useDrop({
      accept: "photo",
      hover(item: { index: number; type: string }, monitor) {
        if (disable) {
          return;
        }
        if (!monitor.isOver({ shallow: true })) {
          return;
        }
        if (!ref.current) {
          return;
        }
        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) {
          return;
        }

        movePhoto(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });
    drag(drop(ref));
    return (
      <div
        ref={ref}
        onDoubleClick={() => onDoubleClick(photo)} // Add double click handler
        style={{
          opacity:
            isDragging && (isSelected || selectedPhotos.includes(index))
              ? 0
              : 1,
        }}
      >
        {" "}
        <div
          key={photo.file.name}
          style={{ width: "100px", textAlign: "center", position: "relative" }}
        >
          <img
            src={photo.thumbnail ?? photo.preview}
            alt={photo.file.name}
            style={{
              width: "100%",
              height: "100px",
              objectFit: "cover",
              borderRadius: "8px",
            }}
          />

          {/* HEIC/HEIF indicator */}
          {photo.isHeic && (
            <div
              style={{
                position: "absolute",
                top: "4px",
                left: "4px",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                fontSize: "8px",
                padding: "2px 4px",
                borderRadius: "3px",
                fontWeight: "bold",
              }}
            >
              {photo.file.type === "image/heic" ? "HEIC" : "HEIF"}
            </div>
          )}

          {allowEdit && (
            <div
              onDoubleClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "8px",
              }}
            >
              <button
                onClick={() => {
                  if (!disable) {
                    return decrementCounter(index);
                  }
                }}
                style={buttonStyle}
              >
                <RemoveCircle />
              </button>
              {/* Current Count Display */}
              <span style={{ margin: "0 10px" }}>{photo.count}</span>

              <button
                onClick={() => {
                  if (!disable) {
                    return incrementCounter(index);
                  }
                }}
                style={buttonStyle}
              >
                <AddCircle />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  },
  arePropsEqual
);

interface PhotoCellProps {
  files: FileWithPreview[];
  columnCount: number;
  movePhoto: (dragIndex: number, hoverIndex: number) => void;
  onDoubleClick: (photo: FileWithPreview) => void;
  incrementCounter: (index: number) => void;
  decrementCounter: (index: number) => void;
  selectedPhotos: number[];
  setSelectedPhotos: React.Dispatch<React.SetStateAction<number[]>>;
  disable: boolean;
}

function PhotoGridCell({
  columnIndex,
  rowIndex,
  style,
  files,
  columnCount,
  movePhoto,
  onDoubleClick,
  incrementCounter,
  decrementCounter,
  selectedPhotos,
  setSelectedPhotos,
  disable,
}: CellComponentProps<PhotoCellProps>) {
  const index = rowIndex * columnCount + columnIndex;
  const photo = files[index];
  if (!photo) return <div style={style} />;
  return (
    <div style={{ ...style, display: "flex", justifyContent: "center", paddingTop: 4 }}>
      <Photo
        photo={photo}
        index={index}
        movePhoto={movePhoto}
        onDoubleClick={onDoubleClick}
        incrementCounter={incrementCounter}
        decrementCounter={decrementCounter}
        selectedPhotos={selectedPhotos}
        setSelectedPhotos={setSelectedPhotos}
        isSelected={selectedPhotos.includes(index)}
        allowEdit={true}
        disable={disable}
      />
    </div>
  );
}

type PhotoListProps = {
  onReorder?: (dragIndex: number, hoverIndex: number | "up" | "down") => void;
  parentRef?: React.RefObject<HTMLElement | null> | null;
  allowEdit?: boolean;
  disable?: boolean;
};

const PhotoList = ({
  onReorder = (_dragIndex: number, _hoverIndex: number | "up" | "down") => {},

  parentRef = null,
  allowEdit = true,
  disable = false,
}: PhotoListProps) => {
  const { files, setFiles } = useFiles();

  const [selectedPhotos, setSelectedPhotos] = useState<number[]>([]);
  // Measured by the virtual grid itself (react-window v2); 360 is a phone-ish
  // guess for the first frame.
  const [gridWidth, setGridWidth] = useState(360);
  const translate = useAppTranslate();
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 768);
  const numColumns = Math.floor(windowWidth / 140); // Assuming each photo is 100px wide + 20px gap
  const numRows = Math.ceil(files.length / numColumns);
  const [selectedPhoto, setSelectedPhoto] = useState<FileWithPreview | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  // ProductContext has been removed - products are now managed via API
  // const { quantities, names } = useProducts();
  const quantities: Record<string, number> = {};
  const names: Record<string, Record<string, string>> = {};
  const currentLocale = useLocale();
  let currentLocaleFixed = currentLocale === "sl" ? "si" : currentLocale;
  currentLocaleFixed = currentLocaleFixed?.split("-")[0];
  const productsMap: Record<
    string,
    { name: Record<string, string>; quantity: number }
  > = {};
  let stringOfProducts = "";
  for (let productId in quantities) {
    if (quantities[productId] === 0) {
      continue;
    }
    productsMap[productId] = {
      name: names[productId],
      quantity: quantities[productId],
    };
    stringOfProducts += ` + ${
      names[productId][currentLocaleFixed ?? "sl"]
    }(${quantities[productId]}x)`;
  }

  const incrementCounter = (index: number) => {
    setFiles((prevFiles) => {
      return prevFiles.map((file, idx) => {
        if (idx === index) {
          return { ...file, count: (file.count ?? 1) + 1 };
        }
        return file;
      });
    });
  };
  const NOfFiles = useMemo(() => {
    return files?.reduce((acc: number, photo: FileWithPreview) => {
      const legacyFileCount = (photo.file as unknown as { count?: number })
        .count;
      if (photo.count) {
        return acc + photo.count;
      } else if (legacyFileCount) {
        return acc + legacyFileCount;
      }
      return acc + 1;
    }, 0);
  }, [files]);

  // "−" on the last copy removes the photo. That used to happen on the tap,
  // which on a phone grid is easily a mis-tap meant for "+", and the photo had
  // to be found and uploaded again. Going below one copy now asks first.
  const [pendingRemoval, setPendingRemoval] = useState<number | null>(null);
  const removalPhoto =
    pendingRemoval !== null ? files[pendingRemoval] : undefined;

  // Read through a ref: the memoised <Photo> ignores handler changes (see
  // arePropsEqual), so it can hold a decrementCounter from an older render.
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const decrementCounter = (index: number) => {
    if ((filesRef.current[index]?.count ?? 1) <= 1) {
      setPendingRemoval(index);
      return;
    }
    setFiles((prevFiles) =>
      prevFiles.map((file, idx) =>
        idx === index ? { ...file, count: (file.count ?? 1) - 1 } : file
      )
    );
  };

  const confirmRemoval = () => {
    if (pendingRemoval !== null) {
      const index = pendingRemoval;
      setFiles((prevFiles) => prevFiles.filter((_, idx) => idx !== index));
    }
    setPendingRemoval(null);
  };

  const handleResize = () => {
    if (parentRef && parentRef.current) {
      const style = window.getComputedStyle(parentRef.current);
      const padLeft = parseInt(style.paddingLeft, 10);
      const padRight = parseInt(style.paddingRight, 10);
      const widthWithPadding = parentRef.current.offsetWidth;
      setWindowWidth(widthWithPadding - padLeft - padRight + 100);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    handleResize();
  }, [parentRef]);

  const moveSelectedPhotos = (direction: "up" | "down") => {
    for (let index = 0; index < selectedPhotos.length; index++) {
      if (direction === "up" && index > 0) {
        onReorder(index, index - 1);
      } else if (direction === "down" && index < files.length - 1) {
        onReorder(index, index + 1);
      }
    }
  };
  const handlePhotoDoubleClick = (photo: FileWithPreview) => {
    setSelectedPhoto(photo);
    setIsModalOpen(true);
  };

  const movePhoto = useCallback(
    throttle((dragIndex: number, hoverIndex: number) => {
      console.error("movePhoto", dragIndex, hoverIndex, selectedPhotos);
      if (selectedPhotos.includes(dragIndex)) {
        moveSelectedPhotos(hoverIndex > dragIndex ? "down" : "up");
        let newSelectedPhotos: number[] = [];
        for (const selectedPhoto of selectedPhotos) {
          newSelectedPhotos.push(selectedPhoto + (hoverIndex - dragIndex));
        }

        setSelectedPhotos(newSelectedPhotos);
      } else {
        onReorder(dragIndex, hoverIndex);
      }
    }, 100),
    [onReorder, selectedPhotos, moveSelectedPhotos]
  );
  const minRowCountAnd2 = Math.min(numRows, 2);
  return (
    <>
      {isModalOpen && selectedPhoto && (
        <PhotoModal
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          photo={selectedPhoto}
        />
      )}
      <Dialog
        open={!!removalPhoto}
        onClose={() => setPendingRemoval(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {translate("photoPreview.removeTitle", "Remove this photo?")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", alignItems: "center", gap: 2 }}
        >
          {removalPhoto?.preview && (
            <Box
              component="img"
              src={removalPhoto.preview}
              alt=""
              sx={{
                width: 72,
                height: 72,
                objectFit: "cover",
                borderRadius: "8px",
                flexShrink: 0,
              }}
            />
          )}
          <Typography variant="body2" color="text.secondary">
            {translate(
              "photoPreview.removeBody",
              "The photo will be removed from your order."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoval(null)}>
            {translate("photoPreview.removeCancel", "Keep it")}
          </Button>
          <Button color="error" variant="contained" onClick={confirmRemoval}>
            {translate("photoPreview.removeConfirm", "Remove")}
          </Button>
        </DialogActions>
      </Dialog>
      <DndProvider backend={HTML5Backend}>
        {allowEdit && files.length > VIRTUALIZE_AFTER ? (
          <Box sx={{ width: "100%" }}>
            {(() => {
              const layout = photoGridLayout(gridWidth);
              const rowCount = Math.ceil(files.length / layout.columnCount);
              return (
                <VirtualGrid
                  cellComponent={PhotoGridCell}
                  cellProps={{
                    files,
                    columnCount: layout.columnCount,
                    movePhoto,
                    onDoubleClick: handlePhotoDoubleClick,
                    incrementCounter,
                    decrementCounter,
                    selectedPhotos,
                    setSelectedPhotos,
                    disable,
                  }}
                  columnCount={layout.columnCount}
                  columnWidth={layout.columnWidth}
                  rowCount={rowCount}
                  rowHeight={layout.rowHeight}
                  overscanCount={2}
                  defaultWidth={gridWidth}
                  style={{
                    width: "100%",
                    height: Math.min(rowCount * layout.rowHeight, GRID_VIEWPORT_HEIGHT),
                  }}
                  onResize={(size) => {
                    if (size.width && Math.abs(size.width - gridWidth) > 1) {
                      setGridWidth(size.width);
                    }
                  }}
                />
              );
            })()}
          </Box>
        ) : allowEdit ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0,
              overflowY: "auto",
              maxHeight: 180 * minRowCountAnd2 + 16,
            }}
          >
            {files.map((photo, index) => (
              <Box
                key={photo.file.name + index}
                sx={{
                  p: { xs: 0.5, sm: 2 },
                  width: { xs: "33.333%", sm: "auto" },
                  boxSizing: "border-box",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Photo
                  photo={photo}
                  index={index}
                  movePhoto={movePhoto}
                  onDoubleClick={handlePhotoDoubleClick}
                  incrementCounter={incrementCounter}
                  decrementCounter={decrementCounter}
                  selectedPhotos={selectedPhotos}
                  setSelectedPhotos={setSelectedPhotos}
                  isSelected={selectedPhotos.includes(index)}
                  allowEdit={allowEdit}
                  disable={disable}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <>
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "16px",
                  marginTop: "16px",
                }}
              >
                {files.map((photo, index) => {
                  if (
                    index < N_OF_PHOTOS_BEGIN ||
                    files.length <= N_OF_PHOTOS_BEGIN + N_OF_PHOTOS_END
                  )
                    return (
                      <Photo
                        key={index}
                        photo={photo}
                        index={index}
                        movePhoto={movePhoto}
                        onDoubleClick={handlePhotoDoubleClick}
                        incrementCounter={incrementCounter}
                        decrementCounter={decrementCounter}
                        selectedPhotos={selectedPhotos}
                        setSelectedPhotos={setSelectedPhotos}
                        isSelected={selectedPhotos.includes(index)}
                        allowEdit={allowEdit}
                        disable={disable}
                      />
                    );
                })}
                {files.length > N_OF_PHOTOS_BEGIN + N_OF_PHOTOS_END && (
                  <Box
                    sx={{
                      width: "100px",
                      height: "100px",
                      bgcolor: "action.selected",
                      color: "text.primary",
                      textAlign: "center",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography paddingTop={"35px"}>
                      {translate("photoPreview.more")}
                    </Typography>
                  </Box>
                )}
                {files.map((photo, index) => {
                  if (
                    index >= files.length - N_OF_PHOTOS_END &&
                    files.length > N_OF_PHOTOS_BEGIN + N_OF_PHOTOS_END
                  )
                    return (
                      <Photo
                        key={index}
                        photo={photo}
                        index={index}
                        movePhoto={movePhoto}
                        onDoubleClick={handlePhotoDoubleClick}
                        incrementCounter={incrementCounter}
                        decrementCounter={decrementCounter}
                        selectedPhotos={selectedPhotos}
                        setSelectedPhotos={setSelectedPhotos}
                        isSelected={selectedPhotos.includes(index)}
                        allowEdit={allowEdit}
                        disable={disable}
                      />
                    );
                })}
              </div>
              {!allowEdit && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Typography>
                    {translate("photoPreview.total")} {NOfFiles}{" "}
                    {translate("photoPreview.photos")} {stringOfProducts}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </DndProvider>
    </>
  );
};
const buttonStyle = {
  background: "none",
  border: "none",
  padding: "0px",
  cursor: "pointer",
  fontSize: "10px",
  color: "inherit",
};

export default PhotoList;
